import React, { useEffect, useRef, useState } from "react";
import styles from "./SiteProgressReportEditor.module.scss";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { DatePicker } from "../../common/DatePicker";
import Editor from "../../common/Editor";
import { MODE_CREATE, MODE_EDIT } from "../../codes";
import moment from "moment";
import useToast from "../../util/useToast";
import { useMutation } from "@tanstack/react-query";
import { DeleteModal } from "../../common/AppModal";
import { ProgressReportErrors } from "../../errors/ErrorsMapping";
import { TextEditor } from "../../lexical/TextEditor";
import {
  apiCreateSiteProgressReport,
  apiDeleteSiteProgressReport,
  apiUpdateSiteProgressreport
} from "../../api/ApiSites";

const fileToBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = () => {
    const result = reader.result;
    const base64 = typeof result === "string" && result.includes(",") ? result.split(",")[1] : result;

    resolve(base64);
  };

  reader.onerror = error => reject(error);

  reader.readAsDataURL(file);
});

const SiteProgressReportEditor = ({ show, mode, hide, site, progressReport, onSubmit = null, onDelete }) => {
  const { showToastSuccess } = useToast();

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    description: "",
    date: moment().format("YYYY-MM-DD")
  });

  const editorRef = useRef(null);

  const createMutation = useMutation(apiCreateSiteProgressReport(site.id));
  const editMutation = useMutation(apiUpdateSiteProgressreport(site.id, progressReport.id));
  const deleteMutation = useMutation(apiDeleteSiteProgressReport(site.id, progressReport.id));

  useEffect(() => {
    if (show) {
      setFormErrors({});
      setSelectedFile(null);

      if (progressReport.fileKey) {
        setPreviewUrl(process.env.REACT_APP_API_URL + "/public/sipr/" + progressReport.fileKey + "?w=200&h=200")
      } else {
        setPreviewUrl(null);
      }

      setFormData({
        siteId: site.id,
        description: progressReport.description || "",
        date: progressReport.date || moment().format("YYYY-MM-DD"),
      });
    }
  }, [show, progressReport, site.id]);

  const submit = async () => {
    let promise;

    const submitData = {
      ...formData,
      description: editorRef.current?.getHtml() || ""
    };

    if (selectedFile) {
      submitData.fileName = selectedFile.name;
      submitData.fileContent = await fileToBase64(selectedFile);
    }

    if (mode === MODE_EDIT) {
      promise = editMutation.mutateAsync(submitData);
    } else {
      promise = createMutation.mutateAsync(submitData);
    }

    return promise
      .then(() => {
        return (onSubmit ? onSubmit() : Promise.resolve()).then(() => {
          if (mode === MODE_EDIT) {
            showToastSuccess("Relevé modifié");
          } else {
            showToastSuccess("Relevé ajouté");
          }
        });
      })
      .then(hide)
      .catch((err) => {
        if (err.response?.status === 400) {
          setFormErrors(err.response.data.errors);
        }
      });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      setSelectedFile(file);

      const objectUrl = URL.createObjectURL(file);

      setPreviewUrl(objectUrl);
    }
  };

  const updateFormData = field => e => {
    const values = {};

    switch (field) {
      case "date":
        values[field] = e;
        break;
      default:
        values[field] = e.target.value;
        break;
    }

    setFormData(prev => ({ ...prev, ...values }));
  };

  return <Editor
    show={show}
    title={mode === MODE_CREATE ? "Ajouter un relevé" : "Modifier un relevé"}
    onSubmit={submit}
    onCancel={hide}
    onDelete={() => setDeleteModal(true)}
    submitLabel="Sauvegarder"
    cancelLabel="Annuler"
  >
    <Form.Group className="mb-2" controlId="site-progress-report-editor-date">
      <Row>
        <Form.Label column sm={3}>Date</Form.Label>
        <Col sm={9}>
          <DatePicker
            value={formData.date}
            onChange={updateFormData("date")}
            isInvalid={!!formErrors.date}
            error={ProgressReportErrors.date[formErrors.date]}
          />
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="site-progress-report-editor-description">
      <Row>
        <Form.Label column sm={3}>Description</Form.Label>
        <Col sm={9}>
          <TextEditor ref={editorRef} value={formData.description} />
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="site-progress-report-editor-photo">
      <Row>
        <Form.Label column sm={3}>Photo</Form.Label>
        <Col sm={9}>
          {!previewUrl && <input type="file" accept="image/*" onChange={handleFileChange} />}

          {previewUrl && (
            <div className={styles.photoPreview}>
              <img
                src={previewUrl}
                alt="Image preview"
                style={{ width: "200px", height: "200px", objectFit: "cover", borderRadius: "8px" }}
              />

              <div className={styles.deletePhoto} onClick={() => {
                setPreviewUrl(null);
                setSelectedFile(null);
              }}>
                <span className="material-icons">close</span>
              </div>
            </div>
          )}
        </Col>
      </Row>
    </Form.Group>

    <DeleteModal
      show={deleteModal}
      title="Supprimer le relevé"
      onCancel={() => setDeleteModal(false)}
      onSubmit={() => {
        return deleteMutation.mutateAsync(null)
          .then(onDelete)
          .then(() => {
            showToastSuccess("Relevé supprimé");

            hide();
            setDeleteModal(false);
          });
      }}
      cancelLabel="Annuler"
      submitLabel="Supprimer"
    >
      Etes-vous sûr de vouloir supprimer ce relevé ?
    </DeleteModal>
  </Editor>;
};

export default SiteProgressReportEditor;